import produce from "immer";
import React from "react";
import { FormattedHTMLMessage, InjectedIntl, injectIntl } from "react-intl";
import { FormFieldConfig, VerificationService } from "../../../lib/types/types";
import { getSafe } from "../../../lib/utils/objects";

import { FormFieldText } from "../FormFieldText/FormFieldText";

export interface FormFieldCustomProps {
  config: FormFieldConfig;
  verificationService: VerificationService;
  intl: InjectedIntl;
}

export const getMessageIdForLabel = (config: FormFieldConfig) => `${config.fieldId}Label`;
export const getMessageIdForPlaceholder = (config: FormFieldConfig) =>
  `${config.fieldId}Placeholder`;

/**
 * Custom form field component.
 * Implementors can render a form field passing in config using setOptions, rather than
 * having to create an entire React project and override component(s).
 */
export const FormFieldCustom = injectIntl(
  ({ config, verificationService, intl }: FormFieldCustomProps) => {
    const currentValue =
      getSafe(() => verificationService.viewModel.metadata[config.fieldId]) || "";
    const errorId = getSafe(() => verificationService.fieldValidationErrors[config.fieldId]);

    const onChange = (value: string) => {
      verificationService.updateViewModel(
        produce(verificationService.viewModel, (draft) => {
          draft.metadata = draft.metadata || {};
          draft.metadata[config.fieldId] = value;
        }),
      );
      verificationService.updateFieldValidationErrors(
        produce(verificationService.fieldValidationErrors, (draft) => {
          const errorId: ReturnType<typeof config.validate> = config.validate(value);
          if (errorId === undefined) {
            delete draft[config.fieldId];
          }
          // Don't set error. Only do that when submit is clicked, same as jslib's built-in fields
        }),
      );
    };

    return (
      <FormFieldText
        fieldId={config.fieldId}
        value={`${currentValue}`}
        onChange={onChange}
        placeholder={intl.formatHTMLMessage({
          id: `${config.fieldId}Placeholder`,
          defaultMessage: config.fieldId,
        })}
        label={
          <FormattedHTMLMessage id={`${config.fieldId}Label`} defaultMessage={config.fieldId} />
        }
        errorMessage={
          errorId && (
            <FormattedHTMLMessage
              id={`errorId.${errorId}`}
              defaultMessage={`Invalid ${config.fieldId}`}
            />
          )
        }
        showPlaceholderAndHideLabel={config.showPlaceholderAndHideLabel}
        required={config.required}
      />
    );
  },
);
